/**
 * Cart token adopt — inbound side of the Rodeo Soul Harmony LP cart handoff.
 *
 * When a user lands on truwestern.com with `?cart=<token>` in the URL
 * (because they came from the Hydrogen LP), this script:
 *   1. Fetches the Storefront API cart by GID using <token>.
 *   2. Clears the current Liquid cart (/cart/clear.js), then copies the
 *      Storefront line items into it via /cart/add.js. Clear + add ensures
 *      items don't accumulate on round-trips (LP -> TW -> LP -> TW...).
 *   3. Strips ?cart= from the URL.
 *   4. Reloads so the cart UI reflects the adopted items.
 *
 * Defensive by design:
 *   - No `?cart=` in URL → early return, zero overhead, zero side effects.
 *   - Any error (invalid token, expired cart, network) → silent fail, the
 *     Liquid cart is never touched.
 *   - Wrapped in try/catch so nothing surfaces to the user.
 *
 * Storefront API token is intentionally public — these tokens are designed
 * to be embedded in client code.
 */
(function () {
  'use strict';

  var STOREFRONT_TOKEN = '1c6f1946a756cb3fde03372ff3bb3277';
  var STOREFRONT_API_URL = '/api/2025-01/graphql.json';
  var STRIP_KEYS = ['cart'];

  function getParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function stripParams(keys) {
    try {
      var url = new URL(window.location.href);
      keys.forEach(function (k) { url.searchParams.delete(k); });
      window.history.replaceState({}, '', url.pathname + (url.search ? url.search : '') + url.hash);
    } catch (e) { /* silent */ }
  }

  async function fetchCart(cartGid) {
    var res = await fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({
        query: 'query GetCart($cartId: ID!) @inContext(country: US) { cart(id: $cartId) { id totalQuantity lines(first: 50) { nodes { quantity merchandise { ... on ProductVariant { id } } attributes { key value } } } } }',
        variables: { cartId: cartGid },
      }),
    });
    if (!res.ok) throw new Error('Storefront API responded ' + res.status);
    var json = await res.json();
    var cart = json && json.data && json.data.cart;
    if (!cart) throw new Error('Cart not found or empty');
    return cart;
  }

  function variantIdNumeric(gid) {
    var m = String(gid || '').match(/\/ProductVariant\/(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  async function clearLiquidCart() {
    var res = await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error('/cart/clear.js responded ' + res.status);
    return res.json();
  }

  async function addToLiquidCart(items) {
    if (!items.length) return;
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    });
    if (!res.ok) throw new Error('/cart/add.js responded ' + res.status);
    return res.json();
  }

  async function adoptCart() {
    var token = getParam('cart');
    if (!token) return;

    try {
      var cartGid = token.indexOf('gid://') === 0
        ? token
        : 'gid://shopify/Cart/' + token;

      var cart = await fetchCart(cartGid);

      var items = cart.lines.nodes
        .map(function (line) {
          var id = variantIdNumeric(line.merchandise && line.merchandise.id);
          if (!id) return null;
          var item = { id: id, quantity: line.quantity };
          // Restore line properties from Storefront cart attributes so items
          // preserve markers like _free_gift across round-trips.
          if (line.attributes && line.attributes.length) {
            var props = {};
            for (var i = 0; i < line.attributes.length; i++) {
              var a = line.attributes[i];
              if (a && a.key) props[a.key] = a.value;
            }
            item.properties = props;
          }
          return item;
        })
        .filter(Boolean);

      if (!items.length) {
        stripParams(STRIP_KEYS);
        return;
      }

      // Replace theme cart entirely with LP cart contents so items don't
      // accumulate on subsequent round-trips between the LP and the theme.
      await clearLiquidCart();
      await addToLiquidCart(items);

      stripParams(STRIP_KEYS);

      // Tell the rest of the page the cart changed. Most themes have either
      // a custom event or do a soft reload; the safest universal step is a
      // full reload of the same URL (now without ?cart=).
      window.location.reload();
    } catch (err) {
      // Silent fail — never break the page for the user.
      if (window.console && console.debug) {
        console.debug('[cart-adopt] silent fail:', err && err.message);
      }
      stripParams(STRIP_KEYS);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', adoptCart);
  } else {
    adoptCart();
  }
})();
